import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { WorkspaceSelect } from '@/components/ui/workspace/field';
import { isTypingTarget } from '@/components/ui/workspace/grid-logic';
import { usePrefersReducedMotion } from '@/components/ui/charts/use-entry-motion';

/**
 * PageCarousel — pages moved through sideways (T-0282, ADR-026 decision 9,
 * E24 design §9). Built once here so the Vendor intelligence report
 * (T-0283) and, later, Health & Readiness page the same way.
 *
 * Shape: an optional header, a strip of page tabs with Previous and Next at
 * its ends, and a track of page cards. Each card is the size of the
 * carousel minus the strip (the parent gives the height; the carousel is
 * `h-full min-h-0`) and scrolls vertically on its own when its content is
 * taller. The track scroll-snaps one page at a time: a swipe, a trackpad
 * drag, the strip, `←`/`→` while the carousel has focus, or the buttons all
 * move it. The current page is derived from where the track settled, so a
 * swipe updates the strip. Under `prefers-reduced-motion` moves are instant.
 *
 * The strip mirrors the approved E24 mockup: number and title in a chip,
 * the current one in ink-900, an origin dot (AI draft in the AI-surface
 * border colour, edited in amber, none for code) and a count of issues. Below
 * 640px the strip becomes a select between the two buttons.
 *
 * Accessibility: the root is a `region` described as a carousel; the strip
 * is a `tablist` of `tab`s (roving tab stop, `aria-selected`); each page is
 * the `tabpanel` its tab labels. Pages that are not current are `inert`, so
 * Tab never lands on content that is scrolled out of view. Nothing depends
 * on hover.
 *
 * Controlled with `current` (the page key) and `onChange`, or uncontrolled
 * with `defaultCurrent`.
 */

export type PageOrigin = 'ai' | 'edited' | 'code';

export interface CarouselPage {
  /** Stable identity, also the value of `current`. Any string. */
  key: string;
  title: string;
  /** Who wrote the page's prose: shown as a dot in the strip. `code` shows nothing. */
  origin?: PageOrigin;
  /** Open issues on the page, shown as a count in the strip when above zero. */
  issues?: number;
}

export interface PageCarouselProps {
  pages: ReadonlyArray<CarouselPage>;
  /** Key of the current page (controlled). */
  current?: string;
  /** Key of the page shown first (uncontrolled). The first page when absent. */
  defaultCurrent?: string;
  onChange?: (key: string, index: number) => void;
  renderPage: (page: CarouselPage, index: number, active: boolean) => React.ReactNode;
  /** Accessible name of the carousel, for example "Report pages". */
  'aria-label': string;
  /** Rendered above the strip: a toolbar, a status line. */
  header?: React.ReactNode;
  className?: string;
}

/** Below this width the strip is a select. */
export const PAGE_CAROUSEL_PHONE_MAX_WIDTH = 639;
const PHONE_QUERY = `(max-width: ${PAGE_CAROUSEL_PHONE_MAX_WIDTH}px)`;

/** Quiet time after the last scroll event before the track counts as settled. */
export const PAGE_CAROUSEL_SETTLE_MS = 100;

const ORIGIN_LABEL: Record<Exclude<PageOrigin, 'code'>, string> = {
  ai: 'AI draft',
  edited: 'Edited',
};

/** Tracks a media query with `matchMedia`; `fallback` where there is none. */
export function useMediaQuery(query: string, fallback = false): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener?.('change', onChange);
      return () => mql.removeEventListener?.('change', onChange);
    },
    [query],
  );
  const getSnapshot = React.useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return fallback;
    return window.matchMedia(query).matches;
  }, [query, fallback]);
  return React.useSyncExternalStore(subscribe, getSnapshot, () => fallback);
}

/** The page whose left edge is nearest the track's scroll position; `NaN` offsets never win. */
export function nearestPageIndex(scrollLeft: number, pageOffsets: ReadonlyArray<number>): number {
  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  pageOffsets.forEach((left, i) => {
    const distance = Math.abs(left - scrollLeft);
    if (distance < bestDistance) {
      best = i;
      bestDistance = distance;
    }
  });
  return best;
}

function issuesLabel(issues: number): string {
  return `${issues} ${issues === 1 ? 'issue' : 'issues'}`;
}

/** The option text at phone width: "2. Executive summary · edited · 1 issue". */
export function pageOptionLabel(page: CarouselPage, index: number): string {
  const parts = [`${index + 1}. ${page.title}`];
  if (page.origin === 'ai') parts.push('AI draft');
  if (page.origin === 'edited') parts.push('edited');
  if (page.issues) parts.push(issuesLabel(page.issues));
  return parts.join(' · ');
}

export function PageCarousel({
  pages,
  current,
  defaultCurrent,
  onChange,
  renderPage,
  'aria-label': ariaLabel,
  header,
  className,
}: PageCarouselProps) {
  const idPrefix = React.useId();
  const phone = useMediaQuery(PHONE_QUERY);
  const reduced = usePrefersReducedMotion();
  const behavior: ScrollBehavior = reduced ? 'auto' : 'smooth';

  const [inner, setInner] = React.useState<string | undefined>(defaultCurrent);
  const controlled = current !== undefined;
  const currentKey = controlled ? current : inner;
  const index = Math.max(
    0,
    pages.findIndex((p) => p.key === currentKey),
  );
  const count = pages.length;

  const stripRef = React.useRef<HTMLDivElement>(null);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const pageRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  /** The page the track was last scrolled to, or last settled on. */
  const scrolledRef = React.useRef<number | null>(null);
  const indexRef = React.useRef(index);
  indexRef.current = index;
  const mountedRef = React.useRef(false);
  const settleTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Focus to move once the page it names is current (and no longer inert). */
  const pendingFocus = React.useRef<{ index: number; what: 'tab' | 'panel' } | null>(null);

  const go = (next: number, focus: 'tab' | 'panel' | 'none' = 'none') => {
    const page = pages[next];
    if (!page) return;
    if (next === index) return;
    if (focus !== 'none') pendingFocus.current = { index: next, what: focus };
    if (!controlled) setInner(page.key);
    onChange?.(page.key, next);
  };
  const goRef = React.useRef(go);
  goRef.current = go;

  // Keyboard moves keep the keyboard: focus follows to the new tab, or to the
  // new page when the move came from inside a page. Done after the commit,
  // because a page cannot take focus until it stops being inert, and without
  // scrolling, so the track's own slide is not pre-empted.
  React.useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return;
    pendingFocus.current = null;
    if (pending.index !== index) return;
    const target = pending.what === 'tab' ? tabRefs.current[index] : pageRefs.current[index];
    target?.focus({ preventScroll: true });
  });

  // The track follows the current page, except when the current page came
  // from the track settling there (then it is already in view). The first
  // move is instant so a page other than the first opens without a slide.
  React.useLayoutEffect(() => {
    const track = trackRef.current;
    const page = pageRefs.current[index];
    const first = !mountedRef.current;
    mountedRef.current = true;
    if (!track || !page || scrolledRef.current === index) return;
    scrolledRef.current = index;
    track.scrollTo({ left: page.offsetLeft, behavior: first ? 'auto' : behavior });
  }, [index, behavior]);

  // Keep the current tab in view when the strip overflows. Done by hand
  // rather than with `scrollIntoView` so the document itself never moves.
  React.useEffect(() => {
    const strip = stripRef.current;
    const tab = tabRefs.current[index];
    if (!strip || !tab) return;
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    let target = strip.scrollLeft;
    if (left < strip.scrollLeft) target = left;
    else if (right > strip.scrollLeft + strip.clientWidth) target = right - strip.clientWidth;
    if (target !== strip.scrollLeft) strip.scrollTo({ left: target, behavior });
  }, [index, behavior, phone]);

  // A resize changes every page's offset: put the current page back in place.
  React.useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const page = pageRefs.current[indexRef.current];
      if (page) track.scrollTo({ left: page.offsetLeft, behavior: 'auto' });
    });
    observer.observe(track);
    return () => observer.disconnect();
  }, []);

  React.useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    },
    [],
  );

  // The current page is derived from where the track settles, so a swipe
  // or a trackpad drag updates the strip.
  const onTrackScroll = () => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      settleTimer.current = null;
      const track = trackRef.current;
      if (!track) return;
      const offsets = pages.map((_, i) => pageRefs.current[i]?.offsetLeft ?? Number.NaN);
      const nearest = nearestPageIndex(track.scrollLeft, offsets);
      if (nearest === scrolledRef.current) return;
      scrolledRef.current = nearest;
      goRef.current(nearest);
    }, PAGE_CAROUSEL_SETTLE_MS);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.altKey || e.ctrlKey || e.metaKey || isTypingTarget(e.target)) return;
    e.preventDefault();
    const target = e.target as Node;
    const from = stripRef.current?.contains(target)
      ? 'tab'
      : trackRef.current?.contains(target)
        ? 'panel'
        : 'none';
    go(e.key === 'ArrowRight' ? index + 1 : index - 1, from);
  };

  const ids = (i: number) => ({ tab: `${idPrefix}-tab-${i}`, panel: `${idPrefix}-panel-${i}` });

  const previous = (
    <WorkspaceButton
      aria-label="Previous page"
      disabled={index <= 0}
      onClick={() => go(index - 1)}
      className="w-[30px] px-0"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
    </WorkspaceButton>
  );
  const next = (
    <WorkspaceButton
      aria-label="Next page"
      disabled={index >= count - 1}
      onClick={() => go(index + 1)}
      className="w-[30px] px-0"
    >
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    </WorkspaceButton>
  );

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn('flex h-full min-h-0 min-w-0 flex-col gap-3', className)}
    >
      {header ? <div className="flex-none">{header}</div> : null}

      <div className="flex flex-none items-center gap-2">
        {previous}
        {phone ? (
          <WorkspaceSelect
            aria-label="Page"
            value={pages[index]?.key ?? ''}
            onChange={(e) => go(pages.findIndex((p) => p.key === e.target.value))}
            className="h-[30px] min-w-0 flex-1 text-body-sm"
          >
            {pages.map((page, i) => (
              <option key={page.key} value={page.key}>
                {pageOptionLabel(page, i)}
              </option>
            ))}
          </WorkspaceSelect>
        ) : (
          <div
            ref={stripRef}
            role="tablist"
            aria-label="Pages"
            aria-orientation="horizontal"
            className="relative flex min-w-0 flex-1 gap-1 overflow-x-auto px-0.5 py-1 [scrollbar-width:thin]"
          >
            {pages.map((page, i) => {
              const on = i === index;
              const id = ids(i);
              return (
                <button
                  key={page.key}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={id.tab}
                  aria-selected={on}
                  aria-controls={id.panel}
                  tabIndex={on ? 0 : -1}
                  onClick={() => go(i)}
                  className={cn(
                    'relative inline-flex h-7 flex-none items-center gap-1.5 whitespace-nowrap rounded-chip border px-2 text-meta',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1',
                    on
                      ? 'border-ink-900 bg-ink-900 font-semibold text-paper-0'
                      : 'border-ink-100 bg-paper-0 font-medium text-ink-700 hover:border-ink-200 hover:bg-paper-50',
                  )}
                >
                  <span className="tabular-nums">{i + 1}</span>
                  <span>{page.title}</span>
                  {page.origin && page.origin !== 'code' ? (
                    <span
                      className={cn(
                        'h-1.5 w-1.5 flex-none rounded-full',
                        page.origin === 'ai' ? 'bg-ai-surface-border' : 'bg-accent-amber-600',
                      )}
                      title={ORIGIN_LABEL[page.origin]}
                    >
                      <span className="sr-only">{ORIGIN_LABEL[page.origin]}</span>
                    </span>
                  ) : null}
                  {page.issues ? (
                    <span className="rounded-full bg-posture-critical-tint px-1.5 py-px text-[11px] font-semibold leading-4 text-posture-critical">
                      <span aria-hidden="true">{page.issues}</span>
                      <span className="sr-only">{issuesLabel(page.issues)}</span>
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
        {next}
      </div>

      <div
        ref={trackRef}
        onScroll={onTrackScroll}
        className="relative flex min-h-0 min-w-0 flex-1 snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {pages.map((page, i) => {
          const active = i === index;
          const id = ids(i);
          return (
            <CarouselPanel
              key={page.key}
              ref={(el) => {
                pageRefs.current[i] = el;
              }}
              id={id.panel}
              active={active}
              {...(phone
                ? { role: 'group', 'aria-label': `Page ${i + 1} of ${count}: ${page.title}` }
                : { role: 'tabpanel', 'aria-labelledby': id.tab })}
            >
              {renderPage(page, i, active)}
            </CarouselPanel>
          );
        })}
      </div>
    </div>
  );
}

PageCarousel.displayName = 'PageCarousel';

interface CarouselPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  active: boolean;
}

/**
 * One page card. `inert` is set by hand: React 18 has no typed `inert`
 * prop, and a string workaround would stop working on React 19.
 */
const CarouselPanel = React.forwardRef<HTMLDivElement, CarouselPanelProps>(
  ({ active, className, children, ...props }, forwardedRef) => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    React.useLayoutEffect(() => {
      ref.current?.toggleAttribute('inert', !active);
    }, [active]);
    return (
      <div
        ref={(el) => {
          ref.current = el;
          if (typeof forwardedRef === 'function') forwardedRef(el);
          else if (forwardedRef) forwardedRef.current = el;
        }}
        tabIndex={active ? 0 : -1}
        data-active={active || undefined}
        className={cn(
          'h-full min-h-0 w-full flex-none snap-start snap-always overflow-y-auto rounded-card border border-ink-100 bg-paper-0',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
CarouselPanel.displayName = 'CarouselPanel';
